import React, { useState } from 'react';
import { Protocol } from '../data/protocols';
import { useLanguage } from '../i18n/LanguageContext';

const SHOW_STEPS_KEY = 'protocols_showSteps';

interface ProtocolWalkthroughProps {
  protocol: Protocol;
  onStart: () => void;
  startLabel?: string;
}

const loadShowSteps = (): boolean => {
  try {
    const saved = localStorage.getItem(SHOW_STEPS_KEY);
    return saved ? JSON.parse(saved) : true;
  } catch {
    return true;
  }
};

const ProtocolWalkthrough: React.FC<ProtocolWalkthroughProps> = ({ protocol, onStart, startLabel }) => {
  const { language, t } = useLanguage();
  const [showSteps, setShowSteps] = useState<boolean>(loadShowSteps);

  const pick = (b: { no: string; en: string }) => (language === 'en' ? b.en : b.no);

  const toggleSteps = () => {
    setShowSteps((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SHOW_STEPS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const cyclesLine = protocol.cycles
    ? `${protocol.cycles} ${t('protocols.cyclesWord')}`
    : protocol.durationSec
      ? `${Math.round(protocol.durationSec / 60)} ${t('protocols.minutesWord')}`
      : '';

  return (
    <div className="text-left">
      <h2 className="text-3xl font-bold mb-1">{pick(protocol.name)}</h2>
      <p className="text-stone-600 mb-4">{pick(protocol.subtitle)}</p>

      <button
        onClick={toggleSteps}
        className="text-sm text-accent hover:text-accent-dark mb-4"
      >
        {showSteps ? t('protocols.hideSteps') : t('protocols.showSteps')}
      </button>

      {showSteps && (
        <div className="space-y-3 mb-6">
          {protocol.phases.map((phase, i) => (
            <div key={i} className="bg-white/60 rounded-2xl p-4">
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-semibold text-stone-700">{pick(phase.label)}</span>
                <span className="text-accent font-medium tabular-nums">{phase.seconds}s</span>
              </div>
              <p className="text-sm text-stone-600">{pick(phase.instruction)}</p>
            </div>
          ))}
          <p className="text-sm text-stone-500">
            {cyclesLine}
            {protocol.settle
              ? ` · ${pick(protocol.settle.instruction)}`
              : ''}
          </p>
        </div>
      )}

      <button
        onClick={onStart}
        className="w-full inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-accent rounded-full hover:bg-accent-dark focus:ring-4 focus:ring-accent/50"
      >
        {startLabel || t('protocols.start')}
      </button>
    </div>
  );
};

export default ProtocolWalkthrough;
